import { useCallback, useEffect, useRef, useState } from 'react';

import {
  answerAgentQuestions,
  answerAgentRun,
  approveAgentRun,
  cancelAgentRun,
  listAgentRuns,
  pauseAgentRun,
  resumeAgentRun,
  saveAgentPlan,
  setAgentMode,
  startAgentRun,
} from '../api/agentRun';
import { pollingInterval } from '../utils/polling';

// Observer for server-side AGENT runs (HS-190). Deliberately shaped like
// `src/tutorial/useTutorialRun.js`: a map keyed by workflow group, a 2 s
// visibility-gated poll while any run is non-terminal, and a `rev`-diffed
// callback. Two things make it SIMPLER than that hook:
//
//   * There is no `reconcileRun` step-artifact merge. A tutorial run reports
//     per-step `artifacts` the client folds into node.data, because the browser
//     owns the graph there. Here the SERVER owns the graph, so a `rev` bump just
//     means "refetch the graph and reload the chat".
//   * There is no launch handshake with a nodeMap. A run starts from a message.
//
// One thing it does that `useTutorialRun` learned from it: the run map is STAMPED
// with the project it was fetched for, because the map's keys are workflow groups
// and therefore project-independent. See the comment on `snap` below.
//
// It executes nothing. A server run's tools run server-side, which is the whole
// point — `panels/chatRounds.js` stays the browser-driven path and is untouched.

const TERMINAL_STATUSES = new Set(['done', 'failed', 'cancelled']);
export const DEFAULT_GROUP = '__default__';

// Module-level so the gated read below returns a STABLE object for a project whose
// runs have not arrived: a fresh `{}` each render would re-fire every consumer's
// `[runs]` effect on a timer.
const EMPTY_RUNS = Object.freeze({});
const EMPTY_SNAP = Object.freeze({ pid: null, runs: EMPTY_RUNS });

// How hard the mount read tries before accepting that a project has no run. Small
// on purpose: this covers the eventually-consistent window after a run is written,
// not a server that is down — the 2 s poll takes over the moment one is found.
const BOOTSTRAP_ATTEMPTS = 3;
const BOOTSTRAP_RETRY_MS = 1000;

export const groupKeyOf = (run) => run?.workflowId ?? DEFAULT_GROUP;

// Pure: is this run still worth polling? `awaiting_input` IS active — it is not
// terminal, and it is precisely the state the user is being asked to act on.
export const isActive = (run) => !!run && !TERMINAL_STATUSES.has(run.status);

// Pure: the module the user should be sent to first, or null. Mirrors the
// backend's `state.oldest_question` — oldest wins, so working through several
// blocked modules has a stable order rather than whichever a dict yielded.
export function firstParked(run) {
  const parked = run?.parkedTiles;
  if (!Array.isArray(parked) || parked.length === 0) return null;
  return [...parked].sort((a, b) =>
    String(a.askedAt || '').localeCompare(String(b.askedAt || ''))
  )[0];
}

// Pure: the oldest call a MANUAL-mode run is holding, or null. The sibling of
// `firstParked` — same shape of problem (several entries on the run record, pick
// one, and the two surfaces that render it must pick the SAME one), so oldest wins
// here too.
//
// It lives here rather than in either renderer because it is now read by BOTH the
// editor's `ChatPanel` band and the dashboard thread's, and a divergence would show
// two different pending decisions for one run — approve on one surface and the other
// is deciding about something else.
//
// The result is shaped like a TOOL CALL (`name`/`input`) so `summarizeToolCall`
// renders it with the same wording the transcript uses for the same call; without
// that the band would describe the action differently from the log of it.
export function firstApproval(run) {
  const pending = run?.pendingApprovals;
  if (!Array.isArray(pending) || pending.length === 0) return null;
  const oldest = [...pending].sort((a, b) =>
    String(a.askedAt || '').localeCompare(String(b.askedAt || ''))
  )[0];
  return { ...oldest, name: oldest.tool, input: oldest.args || {} };
}

// `enabled: false` stands the hook down entirely — no bootstrap read, no poll.
// It exists for the READ-ONLY shared view: `/api/agent-run` is owner-only by
// design (a run acts AS the owner), so a shared tab's bootstrap 403s three times
// and, on a project whose owner has a live run, would poll into a 403 every two
// seconds for as long as the tab stayed open. `DashboardSidebar` already avoids
// this by never binding a shared project's conversation; the project editor had
// no equivalent guard.
export function useAgentRun(
  projectId,
  { onRunUpdate, onRunTerminal, initialRun, enabled = true } = {}
) {
  // The run map is STAMPED with the project it was fetched for, and every read
  // below is gated on that stamp matching the current `projectId`.
  //
  // A snapshot is keyed by workflow GROUP alone (`groupKeyOf`), and the dashboard
  // binds every project to `__default__`, so the keys COLLIDE across projects.
  // Clearing in a `[projectId]` effect is not enough on either end: an effect runs
  // AFTER the render that already painted the previous project's plan, and a
  // `listAgentRuns` response for the project we just left would still be adopted on
  // arrival. Comparing a stamp closes both, and does it from the first render.
  //
  // Without this, a project with no runs of its own showed the PREVIOUS project's
  // run — plan included — indefinitely: `listAgentRuns` returns [], so `adopt` is
  // never called and nothing overwrites the entry.
  const [snap, setSnap] = useState(EMPTY_SNAP);
  const snapRef = useRef(EMPTY_SNAP);
  const lastRevRef = useRef({});
  const terminalNotifiedRef = useRef({});
  const pollRef = useRef(null);
  // The current project, readable from the `[]`-dep callbacks below (the same
  // assign-during-render idiom as `cbRef`).
  const pidRef = useRef(projectId);
  pidRef.current = projectId;
  // Callbacks in a ref so a caller re-creating them inline doesn't restart the
  // poll on every render (the useTutorialRun precedent).
  const cbRef = useRef({ onRunUpdate, onRunTerminal });
  cbRef.current = { onRunUpdate, onRunTerminal };

  // `forPid` is the project the snapshot was FETCHED for — not necessarily the one
  // bound now, which is the whole point.
  const adopt = useCallback((r, forPid) => {
    if (!r) return;
    // A response for a project we have since left. Dropping it is what stops an
    // in-flight fetch from re-contaminating the map after a switch.
    if (forPid !== pidRef.current) return;
    const key = groupKeyOf(r);
    // First adopt for this project: start a fresh map rather than merging onto the
    // previous project's entries. `lastRevRef` must go with it — it is group-keyed
    // too, so a new run whose `rev` collides with the value the previous project
    // left behind would suppress `onRunUpdate` and never reload the transcript.
    const carry = snapRef.current.pid === forPid;
    if (!carry) {
      lastRevRef.current = {};
      terminalNotifiedRef.current = {};
    }
    snapRef.current = {
      pid: forPid,
      runs: { ...(carry ? snapRef.current.runs : {}), [key]: r },
    };
    setSnap(snapRef.current);
    if (r.rev !== lastRevRef.current[key]) {
      lastRevRef.current[key] = r.rev;
      try {
        cbRef.current.onRunUpdate?.(r);
      } catch (err) {
        console.error('agent-run onRunUpdate failed:', err);
      }
    }
    if (TERMINAL_STATUSES.has(r.status) && terminalNotifiedRef.current[key] !== r.runId) {
      terminalNotifiedRef.current[key] = r.runId;
      try {
        cbRef.current.onRunTerminal?.(r);
      } catch (err) {
        console.error('agent-run onRunTerminal failed:', err);
      }
    }
  }, []);

  const refresh = useCallback(async () => {
    // Captured, not read from the ref inside: the await is where the project can
    // change under us, and `adopt` compares this against the ref to notice.
    const forPid = projectId;
    if (!forPid) return;
    try {
      for (const r of (await listAgentRuns(forPid)) || []) adopt(r, forPid);
    } catch {
      /* transient — the next tick retries */
    }
  }, [projectId, adopt]);

  // THE gated read. Everything public is derived from this, so a snapshot belonging
  // to another project is unreachable rather than merely stale — which is what also
  // keeps `ProjectPage`'s `agentRunActive` (and hence the graph autosave it
  // suspends) from being driven by a run in a project the user has left.
  const runs = snap.pid === projectId ? snap.runs : EMPTY_RUNS;

  // Poll while ANY run is non-terminal. `pollingInterval` is visibility-gated, so
  // a backgrounded tab stops polling; the visibilitychange handler refreshes
  // immediately on return rather than waiting out the interval. Derived from the
  // GATED runs, so a foreign active run cannot keep the timer alive.
  const anyActive = Object.values(runs).some(isActive);
  useEffect(() => {
    if (!anyActive) return undefined;
    pollRef.current = pollingInterval(refresh, 2000);
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(pollRef.current);
      pollRef.current = null;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [anyActive, refresh]);

  // A run this component's CALLER started, handed straight to the map instead of
  // waiting for a read to discover it.
  //
  // `start()` below already does this, and for the same reason; `initialRun` is for a
  // run started before the hook was bound to the project — which is exactly what the
  // dashboard composer does, since the project does not exist until it creates one.
  // Passing it matters because the alternative is the bootstrap read, and that read
  // can miss: `listAgentRuns` is an eventually-consistent Query and the run was
  // written milliseconds earlier. The two ways that went wrong were a thread showing
  // no run at all, and one that adopted an already-terminal read and sat on it.
  //
  // Keyed on the run ID ALONE, and that is deliberate. Adopting again when
  // `projectId` changes would take a run handed over for the project being left and
  // file it under the one being entered — the exact cross-contamination the project
  // stamp exists to prevent, reintroduced through the front door by a caller that
  // simply forgot to clear the prop. A given run is adopted once, for whatever is
  // bound at that moment (`pidRef`, not the render's `projectId`, since the effect
  // runs after the render that changed it).
  const initialRunId = initialRun?.runId ?? null;
  useEffect(() => {
    if (initialRun) adopt(initialRun, pidRef.current);
    // `initialRun` and `projectId` are deliberately not dependencies — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRunId, adopt]);

  // On mount, adopt whatever is already running server-side. This is what makes a
  // closed tab a non-event: the run kept going, and reopening the project picks it
  // back up.
  //
  // RETRIED a few times while nothing is known, rather than read once. The read is
  // eventually consistent, so a run written moments ago — by another tab, or by the
  // composer that just created this project — can be absent from it; and nothing
  // would ever look again, because the 2 s poll below is armed off `anyActive` (false
  // while the map is empty) and this effect re-fires only when `projectId` changes.
  // One missed read was therefore permanent. It stops as soon as a run is known, so
  // a project that genuinely has none costs `BOOTSTRAP_ATTEMPTS` reads and no timer.
  useEffect(() => {
    if (!projectId || !enabled) return undefined;
    let alive = true;
    let timer = null;
    let attempt = 0;
    const tick = async () => {
      await refresh();
      attempt += 1;
      if (!alive || attempt >= BOOTSTRAP_ATTEMPTS) return;
      const known = snapRef.current.pid === projectId ? snapRef.current.runs : {};
      if (Object.keys(known).length) return;
      timer = setTimeout(tick, BOOTSTRAP_RETRY_MS);
    };
    tick();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, enabled, refresh]);

  const start = useCallback(
    async (message, opts = {}) => {
      const r = await startAgentRun(projectId, { message, ...opts });
      const key = r?.workflowId ?? opts.workflowId ?? DEFAULT_GROUP;
      terminalNotifiedRef.current[key] = null;
      adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  const cancel = useCallback(
    async (workflowId) => {
      const r = await cancelAgentRun(projectId, workflowId);
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  // Hold / release. `paused` is NOT in TERMINAL_STATUSES, so `isActive` stays true and
  // the 2 s poll keeps running — which is what it is for: a held run still has an
  // in-flight solve to report and a queue whose depth the user is watching.
  const pause = useCallback(
    async (workflowId) => {
      const r = await pauseAgentRun(projectId, workflowId);
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  const resume = useCallback(
    async (workflowId) => {
      const r = await resumeAgentRun(projectId, workflowId);
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  const answer = useCallback(
    async (tileId, value, workflowId) => {
      const r = await answerAgentRun(projectId, { tileId, value, workflowId });
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  // Decide on one call a manual-mode run is holding. Adopting the returned run
  // immediately (rather than waiting for the next 2 s poll) is what makes the
  // approval band disappear on the click instead of a beat later — a control that
  // stays on screen after being pressed reads as a dropped click.
  const approve = useCallback(
    async (approvalId, decision, workflowId) => {
      const r = await approveAgentRun(projectId, { approvalId, decision, workflowId });
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  // Answer the whole REQUIREMENTS ROUND. One call for every question, because that
  // is how the agent asked them and how it has to weigh them — see
  // `answerAgentQuestions`. Adopting the returned run immediately is what makes the
  // card disappear on Submit rather than a poll later.
  const answerQuestions = useCallback(
    async (answers, workflowId, { skipped = false } = {}) => {
      const r = await answerAgentQuestions(projectId, { answers, workflowId, skipped });
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  // Save the user's edited plan document. Throws on a parse failure, carrying the
  // line-numbered errors — the caller shows them beside the textarea. Nothing on the
  // record changes in that case, so there is no partial state to reconcile.
  const savePlan = useCallback(
    async (document, workflowId) => {
      const r = await saveAgentPlan(projectId, { document, workflowId });
      if (r) adopt(r, projectId);
      return r;
    },
    [projectId, adopt]
  );

  // Set the project's sticky permission mode, and mirror it onto the live run.
  // Returns `{agentMode, run}`; `run` is null when nothing is live, which is an
  // ordinary outcome — the row is still written and the next run inherits it.
  const setMode = useCallback(
    async (mode, workflowId) => {
      const res = await setAgentMode(projectId, { mode, workflowId });
      if (res?.run) adopt(res.run, projectId);
      return res;
    },
    [projectId, adopt]
  );

  const runFor = useCallback((groupId) => runs[groupId ?? DEFAULT_GROUP] ?? null, [runs]);

  return {
    runs,
    runFor,
    start,
    cancel,
    pause,
    resume,
    answer,
    answerQuestions,
    approve,
    savePlan,
    setMode,
    refresh,
  };
}
